import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Calendar, Package, TrendingUp, Ship } from 'lucide-react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import AIQueryBox from '../components/reports/AIQueryBox';
import { toast } from 'sonner';
import { baseURL } from '../config/APIConfig';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const backendUrl = baseURL.dashboardApi;

const BookingReport = () => {
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [aiResponse, setAiResponse] = useState(null);

    useEffect(() => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        setDateRange({
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        });
    }, []);

    const handleQuery = async (query) => {
        try {


            const response = await fetch(`${backendUrl}/api/reports/ai-query`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    query,
                    reportType: 'booking',
                    dateRange
                })
            });

            console.log(response)

            if (!response.ok) throw new Error('Query failed');

            const data = await response.json();
            setAiResponse(data);
            toast.success('Query processed successfully!');
        } catch (error) {
            toast.error('Failed to process query');
            console.error(error);
        }
    };

    const metrics = [
        { label: 'Total Bookings', value: '821', change: '+15%', icon: BookOpen, color: 'from-blue-500 to-blue-600' },
        { label: 'Active Shipments', value: '156', change: '+8%', icon: Ship, color: 'from-green-500 to-green-600' },
        { label: 'Total Profit', value: '$535,229', change: '+18%', icon: TrendingUp, color: 'from-purple-500 to-purple-600' },
        { label: 'CO2 Emissions', value: '4,318 MT', change: '+12%', icon: Package, color: 'from-orange-500 to-orange-600' },
    ];

    const lineChartData = {
        labels: ['Aug Week 1', 'Aug Week 2', 'Aug Week 3', 'Aug Week 4', 'Sep Week 1', 'Sep Week 2', 'Sep Week 3', 'Sep Week 4', 'Oct Week 1', 'Oct Week 2'],
        datasets: [
            {
                label: 'Bookings',
                data: [95, 102, 88, 110, 98, 105, 92, 88, 75, 68],
                borderColor: 'rgb(59, 130, 246)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                tension: 0.4
            },
            {
                label: 'Profit ($K)',
                data: [62, 68, 55, 71, 64, 69, 58, 54, 48, 42],
                borderColor: 'rgb(16, 185, 129)',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                tension: 0.4
            }
        ]
    };

    const barChartData = {
        labels: ['MSC', 'CMA-CGM', 'Hapag Lloyd', 'Yang Ming', 'Maersk', 'ONE', 'OOCL', 'HMM'],
        datasets: [{
            label: 'Bookings by Carrier',
            data: [550, 62, 38, 28, 25, 19, 16, 13],
            backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316']
        }]
    };

    const doughnutChartData = {
        labels: ['MANAGEMENT', 'NJ SALES', 'Pam Donovan', 'WAF SALES', 'Others'],
        datasets: [{
            data: [502, 130, 72, 33, 84],
            backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#6B7280']
        }]
    };

    // Top Customers Data
    const topCustomers = [
        { name: 'CRAIN RANCH-LOS MOLINOS', bookings: 92, profit: 48250, carrier: 'MSC' },
        { name: 'GAMECHANGE SOLAR-NORWALK', bookings: 72, profit: 38900, carrier: 'CMA-CGM' },
        { name: 'MAK INTERNATIONAL SHIPPING', bookings: 61, profit: 32150, carrier: 'MSC' },
        { name: 'LONCIN MOTOR CO.,LTD.', bookings: 48, profit: 28600, carrier: 'Hapag Lloyd' },
        { name: 'BLUE DIAMOND GROWERS', bookings: 31, profit: 21400, carrier: 'Yang Ming' },
    ];

    // Profit by Carrier Data
    const profitByCarrier = [
        { carrier: 'MSC', profit: 362435, bookings: 550, avgProfit: 659 },
        { carrier: 'CMA-CGM', profit: 30892, bookings: 62, avgProfit: 498 },
        { carrier: 'Yang Ming', profit: 24544, bookings: 28, avgProfit: 877 },
        { carrier: 'Hapag Lloyd', profit: 23509, bookings: 38, avgProfit: 619 },
        { carrier: 'APL', profit: 18935, bookings: 11, avgProfit: 1721 },
    ];

    // Recent Bookings
    const recentBookings = [
        { refNo: 'CN2250327', bookingNo: '07638/2025', date: '2025/08/01', customer: 'SHANDONG HUASHENG PESTICIDE', carrier: 'CSA', pol: 'XIAN', pod: 'MILANO', profit: 216.93, status: 'In Transit' },
        { refNo: 'NJ2250850', bookingNo: 'EBKG13780854', date: '2025/08/01', customer: 'FARMERS INTERNATIONAL', carrier: 'MSC', pol: 'OAKLAND', pod: 'GIOIA TAURO', profit: 437.00, status: 'In Transit' },
        { refNo: 'NJ2250852', bookingNo: 'EBKG13781813', date: '2025/08/01', customer: 'GAF-PARSIPPANY', carrier: 'MSC', pol: 'OAKLAND', pod: 'PORT KLANG', profit: 870.40, status: 'Delivered' },
        { refNo: 'CN2250328', bookingNo: '20250239', date: '2025/08/04', customer: 'SHANGHAI DUNSCH INDUSTRIAL', carrier: 'PMLOG', pol: 'CHANGSHA', pod: 'MILANO', profit: 331.93, status: 'In Transit' },
        { refNo: 'CN2250330', bookingNo: '07668/2025', date: '2025/08/04', customer: 'TIANJIN WANDA TYRE GROUP', carrier: 'ONE', pol: 'XINGANG', pod: 'GENOA', profit: 710.74, status: 'In Transit' },
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
        >
            {/* Header */}
            <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl shadow-lg p-8 text-white">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <BookOpen className="w-8 h-8" />
                        <div>
                            <h1 className="text-3xl font-bold">Booking Report</h1>
                            <p className="text-white/90 text-lg">AI-powered booking analytics and performance insights</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5" />
                        <div className="flex items-center space-x-2">
                            <input
                                type="date"
                                value={dateRange.start}
                                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                                className="px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                            />
                            <span className="text-white/70">to</span>
                            <input
                                type="date"
                                value={dateRange.end}
                                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                                className="px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {metrics.map((metric, index) => {
                    const Icon = metric.icon;
                    return (
                        <motion.div
                            key={index}
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: index * 0.1 }}
                            className={`bg-gradient-to-br ${metric.color} rounded-xl shadow-lg p-6 text-white`}
                        >
                            <div className="flex items-center space-x-2 mb-2">
                                <Icon className="w-5 h-5" />
                                <h3 className="text-sm font-medium opacity-80">{metric.label}</h3>
                            </div>
                            <p className="text-3xl font-bold">{metric.value}</p>
                            <p className="text-sm opacity-80 mt-1">{metric.change} from last period</p>
                        </motion.div>
                    );
                })}
            </div>

            {/* AI Query Box */}
            <AIQueryBox onQuery={handleQuery} reportType="booking" />

            {/* AI Response */}
            {aiResponse && (
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl shadow-sm p-6 border border-green-200 dark:border-green-800"
                >
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3 flex items-center">
                        <svg className="w-6 h-6 mr-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        AI Insights
                    </h3>
                    <div className="prose dark:prose-invert max-w-none">
                        <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{aiResponse.response}</p>
                    </div>
                </motion.div>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
                >
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Booking & Profit Trend</h3>
                    <Line data={lineChartData} options={{ responsive: true, maintainAspectRatio: true }} />
                </motion.div>

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
                >
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Bookings by Carrier</h3>
                    <Bar data={barChartData} options={{ responsive: true, maintainAspectRatio: true }} />
                </motion.div>

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
                >
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Sales by Person</h3>
                    <Doughnut data={doughnutChartData} options={{ responsive: true, maintainAspectRatio: true }} />
                </motion.div>
            </div>

            {/* AI-Driven Booking Insights */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl shadow-sm p-6 border border-purple-200 dark:border-purple-800"
            >
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                    <svg className="w-7 h-7 mr-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    AI-Driven Booking Insights
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                        <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2">🔥 Top Performing Carrier</h4>
                        <p className="text-slate-700 dark:text-slate-300 text-sm">
                            <strong>MSC</strong> dominates with 550 bookings (67% market share) generating $362K profit. Consider negotiating volume discounts.
                        </p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-green-200 dark:border-green-800">
                        <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">💰 Highest Profit Customer</h4>
                        <p className="text-slate-700 dark:text-slate-300 text-sm">
                            <strong>CRAIN RANCH</strong> leads with 92 bookings. Strengthen relationship and explore upsell opportunities for agricultural exports.
                        </p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
                        <h4 className="font-semibold text-orange-700 dark:text-orange-400 mb-2">📊 Booking Trend Alert</h4>
                        <p className="text-slate-700 dark:text-slate-300 text-sm">
                            Bookings declining since September. Focus on customer retention and marketing to reverse the downward trend.
                        </p>
                    </div>
                </div>
                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                        <strong>Key Recommendation:</strong> MANAGEMENT team handles 61% of bookings. Consider redistributing workload to specialized sales teams for better customer service and higher conversion rates. APL offers highest avg profit per booking ($1,721) - prioritize this carrier for premium shipments.
                    </p>
                </div>
            </motion.div>

            {/* Top Customers */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
            >
                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4">
                    <h3 className="text-xl font-bold text-white">Top 5 Customers by Booking Volume</h3>
                </div>
                <div className="p-6">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-slate-200 dark:border-slate-700">
                                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Customer</th>
                                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Bookings</th>
                                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Profit ($)</th>
                                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Primary Carrier</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topCustomers.map((customer, index) => (
                                    <tr key={index} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                        <td className="py-3 px-4 text-sm text-slate-900 dark:text-white font-medium">{customer.name}</td>
                                        <td className="py-3 px-4 text-sm text-center text-slate-700 dark:text-slate-300">{customer.bookings}</td>
                                        <td className="py-3 px-4 text-sm text-center font-semibold text-green-600 dark:text-green-400">${customer.profit.toLocaleString()}</td>
                                        <td className="py-3 px-4 text-sm text-center text-slate-700 dark:text-slate-300">{customer.carrier}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>

            {/* Profit by Carrier Analysis */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
            >
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4">
                    <h3 className="text-xl font-bold text-white">Carrier Performance Analysis</h3>
                </div>
                <div className="p-6">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-slate-200 dark:border-slate-700">
                                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Carrier</th>
                                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Total Profit ($)</th>
                                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Bookings</th>
                                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Avg Profit/Booking ($)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {profitByCarrier.map((carrier, index) => (
                                    <tr key={index} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                        <td className="py-3 px-4 text-sm text-slate-900 dark:text-white font-medium">{carrier.carrier}</td>
                                        <td className="py-3 px-4 text-sm text-center font-semibold text-green-600 dark:text-green-400">${carrier.profit.toLocaleString()}</td>
                                        <td className="py-3 px-4 text-sm text-center text-slate-700 dark:text-slate-300">{carrier.bookings}</td>
                                        <td className="py-3 px-4 text-sm text-center font-semibold text-blue-600 dark:text-blue-400">${carrier.avgProfit}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>

            {/* Recent Bookings List */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.9 }}
                className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden"
            >
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4">
                    <h3 className="text-xl font-bold text-white">Recent Booking List</h3>
                </div>
                <div className="p-6">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-slate-200 dark:border-slate-700">
                                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Ref No</th>
                                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Booking No</th>
                                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Date</th>
                                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Customer</th>
                                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Carrier</th>
                                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">POL → POD</th>
                                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Profit ($)</th>
                                    <th className="text-center py-3 px-4 text-sm font-bold text-slate-900 dark:text-white">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentBookings.map((booking, index) => (
                                    <tr key={index} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                        <td className="py-3 px-4 text-sm font-medium text-blue-600 dark:text-blue-400">{booking.refNo}</td>
                                        <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300">{booking.bookingNo}</td>
                                        <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300">{booking.date}</td>
                                        <td className="py-3 px-4 text-sm text-slate-900 dark:text-white font-medium">{booking.customer}</td>
                                        <td className="py-3 px-4 text-sm text-center text-slate-700 dark:text-slate-300">{booking.carrier}</td>
                                        <td className="py-3 px-4 text-sm text-center text-slate-700 dark:text-slate-300">{booking.pol} → {booking.pod}</td>
                                        <td className="py-3 px-4 text-sm text-center font-semibold text-green-600 dark:text-green-400">${booking.profit.toFixed(2)}</td>
                                        <td className="py-3 px-4 text-center">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${booking.status === 'Delivered' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                                                'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                                }`}>
                                                {booking.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default BookingReport;